import React, { useMemo, useState } from "react";

function SellerAnalyticsPage({
  users,
  auctions,
  dateFilteredAuctions,
  dateFilteredTransactions,
  getTransactionAllocation,
}) {
  const [sellerSearch, setSellerSearch] = useState("");

  const getTime = (value) => {
    if (!value) return 0;

    if (value?.seconds) {
      return value.seconds * 1000;
    }

    return Number(value || 0);
  };

  const formatDate = (value) => {
    const time = getTime(value);

    if (!time) return "—";

    return new Date(time).toLocaleString();
  };

  const formatKWD = (value) => {
    return `${Number(value || 0).toFixed(3)} KWD`;
  };

  const sellerAnalytics = useMemo(() => {
    const sellerMap = {};
    const sellerRevenueMap = {};

    dateFilteredTransactions.forEach((transaction) => {
      const transactionAmount = Number(
        transaction.amountKWD || transaction.amount || 0
      );

      if (transaction.status !== "succeeded") return;
      if (transactionAmount >= 0) return;

      const allocation = getTransactionAllocation(transaction);

      if (
        allocation.label !== "Revenue" &&
        allocation.label !== "Mixed Revenue / Free Grant"
      ) {
        return;
      }

      const paidRevenue = Number(allocation?.paidImpact || 0);

      if (paidRevenue <= 0) return;

      const matchedUser = users.find(
        (user) =>
          user.uid === transaction.uid ||
          user.id === transaction.uid ||
          user.email === transaction.email
      );

      const sellerKey =
        matchedUser?.email ||
        matchedUser?.username ||
        transaction.uid ||
        transaction.email ||
        "";

      if (!sellerKey) return;

      sellerRevenueMap[sellerKey] =
        Number(sellerRevenueMap[sellerKey] || 0) + paidRevenue;
    });

    dateFilteredAuctions.forEach((auction) => {
      const sellerEmail = auction.createdBy || "";
      const sellerUsername = auction.createdByUsername || "";
      const sellerKey = sellerEmail || sellerUsername || "unknown";

      if (!sellerMap[sellerKey]) {
        const matchedUser = users.find(
          (user) =>
            user.email === sellerEmail ||
            user.username === sellerUsername
        );

        sellerMap[sellerKey] = {
          sellerKey,
          username:
            sellerUsername ||
            matchedUser?.username ||
            "Unknown Seller",
          email:
            sellerEmail ||
            matchedUser?.email ||
            "—",
          auctionsCreated: 0,
          openAuctions: 0,
          closedAuctions: 0,
          removedAuctions: 0,
          successfulAuctions: 0,
          liveAuctions: 0,
          flaggedAuctions: 0,
          reportedAuctions: 0,
          reportsReceived: 0,
          totalRevenue: Number(
            sellerRevenueMap[sellerEmail] ||
              sellerRevenueMap[sellerUsername] ||
              sellerRevenueMap[sellerKey] ||
              0
          ),
          totalBids: 0,
          highestBid: 0,
          averageRating: Number(matchedUser?.averageRating || 0),
          lastAuctionDate: 0,
        };
      }

      sellerMap[sellerKey].auctionsCreated += 1;

      if (auction.status === "open") {
        sellerMap[sellerKey].openAuctions += 1;
      }

      if (auction.status === "closed") {
        sellerMap[sellerKey].closedAuctions += 1;
      }

      if (auction.status === "removed") {
        sellerMap[sellerKey].removedAuctions += 1;
      }

      if (auction.closureReason === "winner_declared") {
        sellerMap[sellerKey].successfulAuctions += 1;
      }

      if (auction.live === true) {
        sellerMap[sellerKey].liveAuctions += 1;
      }

      if (auction.flagged === true) {
        sellerMap[sellerKey].flaggedAuctions += 1;
      }

      if (
        auction.reported === true ||
        Number(auction.reportCount || 0) > 0
      ) {
        sellerMap[sellerKey].reportedAuctions += 1;
      }

      sellerMap[sellerKey].reportsReceived += Number(
        auction.reportCount || 0
      );


      sellerMap[sellerKey].totalBids += Number(
        auction.bidHistory?.length ||
          auction.bids?.length ||
          auction.bidAmounts?.length ||
          0
      );

      sellerMap[sellerKey].highestBid = Math.max(
        Number(sellerMap[sellerKey].highestBid || 0),
        Number(auction.currentBid || 0)
      );

      const auctionTime = getTime(auction.createdAt);

      if (auctionTime > sellerMap[sellerKey].lastAuctionDate) {
        sellerMap[sellerKey].lastAuctionDate = auctionTime;
      }
    });

    return Object.values(sellerMap)
      .map((seller) => {
        const sellThroughRate =
        seller.auctionsCreated > 0
          ? (seller.closedAuctions / seller.auctionsCreated) * 100
          : 0;

      const sellerSuccessRate =
        seller.closedAuctions > 0
          ? (seller.successfulAuctions / seller.closedAuctions) * 100
          : 0;

      const averageRevenuePerAuction =
          seller.auctionsCreated > 0
            ? seller.totalRevenue / seller.auctionsCreated
            : 0;

        return {
          ...seller,
          sellThroughRate,
          sellerSuccessRate,
          averageRevenuePerAuction,
        };
      })
      .sort((a, b) => Number(b.totalRevenue || 0) - Number(a.totalRevenue || 0));
    }, [
      users,
      dateFilteredAuctions,
      dateFilteredTransactions,
      getTransactionAllocation,
    ]);

  const filteredSellers = sellerAnalytics.filter((seller) => {
    const searchText = sellerSearch.toLowerCase().trim();

    if (!searchText) return true;

    return (
      String(seller.username || "").toLowerCase().includes(searchText) ||
      String(seller.email || "").toLowerCase().includes(searchText)
    );
  });

  const topSellerByRevenue = sellerAnalytics[0] || null;

  const topSellerByAuctions =
    sellerAnalytics.length > 0
      ? [...sellerAnalytics].sort(
          (a, b) => Number(b.auctionsCreated || 0) - Number(a.auctionsCreated || 0)
        )[0]
      : null;

  const topSellerByRating =
    sellerAnalytics.filter((seller) => Number(seller.averageRating || 0) > 0)
      .length > 0
      ? [...sellerAnalytics]
          .filter((seller) => Number(seller.averageRating || 0) > 0)
          .sort(
            (a, b) =>
              Number(b.averageRating || 0) -
              Number(a.averageRating || 0)
          )[0]
      : null;

  const totalSellerRevenue = sellerAnalytics.reduce(
    (sum, seller) => sum + Number(seller.totalRevenue || 0),
    0
  );

  return (
    <>
      <section className="stats-grid">
        <div className="stat-card">
          <span>Total Sellers</span>
          <strong>{sellerAnalytics.length}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Based on Global Date Range
          </small>
        </div>

        <div className="stat-card">
          <span>Total Seller Revenue</span>
          <strong>{formatKWD(totalSellerRevenue)}</strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Auction + extension revenue
          </small>
        </div>

        <div className="stat-card">
          <span>Top Revenue Seller</span>
          <strong>
            {topSellerByRevenue
              ? topSellerByRevenue.username || "Unknown Seller"
              : "—"}
          </strong>
          <small style={{ display: "block", marginTop: 4 }}>
            {topSellerByRevenue
              ? formatKWD(topSellerByRevenue.totalRevenue)
              : "—"}
          </small>
        </div>

        <div className="stat-card">
          <span>Most Active Seller</span>
          <strong>
            {topSellerByAuctions
              ? topSellerByAuctions.username || "Unknown Seller"
              : "—"}
          </strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Auctions:{" "}
            {topSellerByAuctions
              ? Number(topSellerByAuctions.auctionsCreated || 0)
              : 0}
          </small>
        </div>

        <div className="stat-card">
          <span>Highest Rated Seller</span>
          <strong>
            {topSellerByRating
              ? topSellerByRating.username || "Unknown Seller"
              : "—"}
          </strong>
          <small style={{ display: "block", marginTop: 4 }}>
            Rating:{" "}
            {topSellerByRating
              ? `${Number(topSellerByRating.averageRating || 0).toFixed(1)} ⭐`
              : "—"}
          </small>
        </div>

        <div className="stat-card">
          <span>Removed Seller Auctions</span>
          <strong>
            {sellerAnalytics.reduce(
              (sum, seller) => sum + Number(seller.removedAuctions || 0),
              0
            )}
          </strong>
        </div>
      </section>

      <section className="table-card">
        <div className="table-header">
          <div>
            <h2>Seller Analytics</h2>
            <p>
              Seller performance based on auctions, revenue, reports, flags, and ratings.
            </p>
          </div>
        </div>

        <div className="controls-row">
          <input
            className="search-input"
            placeholder="Search seller username or email..."
            value={sellerSearch}
            onChange={(event) => setSellerSearch(event.target.value)}
          />
        </div>

        {filteredSellers.length === 0 ? (
          <p className="empty-text">No seller analytics found.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Seller</th>
                  <th>Email</th>
                  <th>Auctions</th>
                  <th>Open</th>
                  <th>Closed</th>
                  <th>Successful</th>
                  <th>Removed</th>
                  <th>Live</th>
                  <th>Flagged</th>
                  <th>Reported</th>
                  <th>Reports</th>
                  <th>Total Revenue</th>
                  <th>Avg Revenue / Auction</th>
                  <th>Total Bids</th>
                  <th>Highest Bid</th>
                  <th>Sell-Through Rate</th>
                  <th>Seller Success Rate</th>
                  <th>Rating</th>
                  <th>Last Auction</th>
                </tr>
              </thead>

              <tbody>
                {filteredSellers.map((seller) => (
                  <tr key={seller.sellerKey}>
                    <td>{seller.username || "Unknown Seller"}</td>
                    <td>{seller.email || "—"}</td>
                    <td>{Number(seller.auctionsCreated || 0)}</td>
                    <td>{Number(seller.openAuctions || 0)}</td>
                    <td>{Number(seller.closedAuctions || 0)}</td>
                    <td>{Number(seller.successfulAuctions || 0)}</td>
                    <td>{Number(seller.removedAuctions || 0)}</td>
                    <td>{Number(seller.liveAuctions || 0)}</td>
                    <td>{Number(seller.flaggedAuctions || 0)}</td>
                    <td>{Number(seller.reportedAuctions || 0)}</td>
                    <td>{Number(seller.reportsReceived || 0)}</td>
                    <td>{formatKWD(seller.totalRevenue)}</td>
                    <td>{formatKWD(seller.averageRevenuePerAuction)}</td>
                    <td>{Number(seller.totalBids || 0)}</td>
                    <td>{formatKWD(seller.highestBid)}</td>
                    <td>{Number(seller.sellThroughRate || 0).toFixed(1)}%</td>
                    <td>{Number(seller.sellerSuccessRate || 0).toFixed(1)}%</td>
                    <td>
                      {Number(seller.averageRating || 0) > 0
                        ? `${Number(seller.averageRating || 0).toFixed(1)} ⭐`
                        : "—"}
                    </td>
                    <td>{formatDate(seller.lastAuctionDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default SellerAnalyticsPage;